import { IBoard, IBoardStyle, MessageCard, Spacer, createBoard } from '@vestaboard/installables'
import { Box, Button, CircularProgress, Snackbar, Tooltip, Typography } from '@material-ui/core'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import W3CWebSocket, { CloseEvent } from 'reconnecting-websocket'

import { Alert } from '@material-ui/lab'
import MuiAlert from '@material-ui/lab/Alert'
import { Link } from '../../link'
import { useGetDisplayGroup } from '../hooks/useGetDisplayGroup'
import { useBoardDetailQuery } from '../../board/hooks/useBoardDetailQuery'
import { useGenerateWebsocketAuthToken } from '../../../api/useGenerateWebsocketAuthToken'
import { useEnableDeviceAcks, useDisableDeviceAcks } from '../../../api/useDeviceAcks'
import { useParams } from 'react-router'
import { v4 as uuid } from 'uuid'
import { WS_BASE_URL } from '../../../env'

enum EDeviceMessageType {
  DisplayCommand = 'DeviceMessageDisplayCommand',
  Heartbeat = 'DeviceHeartbeatMessage',
  BoardLayoutChanged = 'DeviceBoardLayoutChangedMessage',
  Refresh = 'DeviceRefreshCommand',
  Identify = 'DeviceIdentifyMessage'
}

const constructDeviceMessage = (deviceId: string, type: EDeviceMessageType, payload: {}) => {
  return JSON.stringify({
    message: {
      id: uuid(),
      type,
      deviceId,
      clientTime: new Date().getTime(),
      maskStatus: 'On',
      ...payload
    }
  })
}

const constructHeartbeatMessage = (deviceId: string) => {
  return constructDeviceMessage(deviceId, EDeviceMessageType.Heartbeat, {
    created: new Date().getTime()
  })
}

const constructIdentifyMessage = (deviceId: string, boardId: string) => {
  return constructDeviceMessage(deviceId, EDeviceMessageType.Identify, { boardId })
}

interface IBoardLiveCell {
  boardId: string
  title: string
  boardStyle: string | null
  connectDelay: number
  onDeviceIdResolved?: (boardId: string, deviceId: string) => void
}

const LiveBoardCell = ({ boardId, title, boardStyle, connectDelay, onDeviceIdResolved }: IBoardLiveCell) => {
  const { data, loading } = useBoardDetailQuery(boardId)
  const [generateAuthToken] = useGenerateWebsocketAuthToken()
  const [enableAcks] = useEnableDeviceAcks()
  const [disableAcks] = useDisableDeviceAcks()
  const [ackLoading, setAckLoading] = useState(false)
  const [ackMessage, setAckMessage] = useState<{ text: string; severity: 'success' | 'error' } | null>(null)
  const generateAuthTokenRef = useRef(generateAuthToken)
  generateAuthTokenRef.current = generateAuthToken
  const [authToken, setAuthToken] = useState<string | null>(null)
  const [characters, setCharacters] = useState<Array<number[]>>(createBoard())
  const [displayAt, setDisplayAt] = useState<number | null>(null)
  const [connected, setConnected] = useState(false)
  const [connectionFailed, setConnectionFailed] = useState(false)

  const deviceId = data?.board?.devices?.[0]?.id
  const pairingCode = data?.board?.devices?.[0]?.pairingCode

  useEffect(() => {
    if (deviceId && onDeviceIdResolved) {
      onDeviceIdResolved(boardId, deviceId)
    }
  }, [deviceId, boardId, onDeviceIdResolved])

  useEffect(() => {
    if (!deviceId || !pairingCode) return

    let cancelled = false

    const delayId = setTimeout(() => {
      generateAuthTokenRef.current({
        variables: {
          deviceId,
          pairingCode
        }
      })
        .then(result => {
          if (!cancelled && result.data?.generateWebsocketAuthTokenV2) {
            setAuthToken(result.data.generateWebsocketAuthTokenV2)
          }
        })
        .catch(err => {
          console.error(`Failed to generate WebSocket auth token for board ${boardId}:`, err)
        })
    }, connectDelay)

    return () => {
      cancelled = true
      clearTimeout(delayId)
    }
  }, [deviceId, pairingCode, boardId, connectDelay])

  useEffect(() => {
    if (!deviceId || !authToken) {
      return
    }

    let disposed = false
    const timeoutIds: ReturnType<typeof setTimeout>[] = []
    let displayAtTimeout: ReturnType<typeof setTimeout> | null = null

    const client = new W3CWebSocket(
      `${WS_BASE_URL}/${deviceId}?X-Vestaboard-Api-Auth=${authToken}`,
      undefined,
      {
        maxReconnectionDelay: 5000,
        minReconnectionDelay: 1000,
        reconnectionDelayGrowFactor: 1.3,
        connectionTimeout: 10000,
        maxRetries: 10,
        debug: false
      }
    )

    const sendHeartbeat = () => {
      if (disposed) return
      const message = constructHeartbeatMessage(deviceId)
      client.send(message)
    }

    const sendIdentify = () => {
      if (disposed) return
      const message = constructIdentifyMessage(deviceId, boardId)
      client.send(message)
    }

    const scheduleHeartbeat = () => {
      const id = setTimeout(() => {
        if (disposed) return
        sendHeartbeat()
        scheduleHeartbeat()
      }, 60 * 1000)
      timeoutIds.push(id)
    }

    client.onopen = () => {
      if (disposed) return
      setConnected(true)
      sendHeartbeat()
      sendIdentify()
      scheduleHeartbeat()
    }

    client.onmessage = (message: any) => {
      if (disposed) return
      const json = JSON.parse(message.data)
      if (json.message.type === EDeviceMessageType.DisplayCommand) {
        const displayAtMs = json.message.displayAt
        setDisplayAt(displayAtMs ?? null)
        const applyDisplay = () => setCharacters(json.message.characters.characters)
        if (displayAtMs) {
          const delayMs = displayAtMs - Date.now()
          if (displayAtTimeout) clearTimeout(displayAtTimeout)
          if (delayMs > 0) {
            displayAtTimeout = setTimeout(applyDisplay, delayMs)
          } else {
            applyDisplay()
          }
        } else {
          applyDisplay()
        }
      }
    }

    client.onclose = (event: CloseEvent) => {
      if (disposed) return
      setConnected(false)
      if (event.code === 1008) {
        console.warn(`WebSocket for board ${boardId} closed with 1008 - backing off`)
        client.close()
      }
      if (client.retryCount >= 10) {
        setConnectionFailed(true)
      }
    }

    return () => {
      disposed = true
      timeoutIds.forEach(clearTimeout)
      if (displayAtTimeout) clearTimeout(displayAtTimeout)
      client.close()
    }
  }, [deviceId, boardId, authToken])

  const hasNoDevice = !loading && (!data?.board?.devices || data.board.devices.length === 0)

  if (loading) {
    return (
      <Box style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: 16 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  return (
    <Box style={{ position: 'relative' }}>
      <MessageCard characters={characters as IBoard} boardStyle={boardStyle as IBoardStyle} />
      {(hasNoDevice || connectionFailed) && (
        <Box
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.6)',
            borderRadius: 4
          }}>
          <Typography variant='body2' style={{ color: '#fff', textAlign: 'center', padding: 8 }}>
            {hasNoDevice ? 'No device paired to board' : 'WebSocket connection failed after 10 retries'}
          </Typography>
        </Box>
      )}
      <Box style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
        <Box
          style={{
            borderRadius: '50%',
            width: 8,
            height: 8,
            backgroundColor: hasNoDevice || connectionFailed ? 'red' : connected ? 'green' : 'grey'
          }}
        />
        <Typography variant='caption'>{title || boardId}</Typography>
      </Box>
      <Typography variant='caption'>
        Display At: {displayAt ? new Date(displayAt).toLocaleString() + '.' + String(displayAt % 1000).padStart(3, '0') : 'null'}
      </Typography>
      {deviceId && (
        <Box style={{ display: 'flex', gap: 4, marginTop: 4 }}>
          <Tooltip title='The ack state is managed on device' arrow>
            <Button
              size='small'
              variant='outlined'
              disabled={ackLoading}
              onClick={async () => {
                setAckLoading(true)
                try {
                  await enableAcks({ variables: { device: deviceId } })
                  setAckMessage({ text: 'Acks enabled', severity: 'success' })
                } catch {
                  setAckMessage({ text: 'Failed to enable acks', severity: 'error' })
                }
                setAckLoading(false)
              }}>
              Enable Acks
            </Button>
          </Tooltip>
          <Tooltip title='The ack state is managed on device' arrow>
            <Button
              size='small'
              variant='outlined'
              disabled={ackLoading}
              onClick={async () => {
                setAckLoading(true)
                try {
                  await disableAcks({ variables: { device: deviceId } })
                  setAckMessage({ text: 'Acks disabled', severity: 'success' })
                } catch {
                  setAckMessage({ text: 'Failed to disable acks', severity: 'error' })
                }
                setAckLoading(false)
              }}>
              Disable Acks
            </Button>
          </Tooltip>
        </Box>
      )}
      <Snackbar open={!!ackMessage} autoHideDuration={3000} onClose={() => setAckMessage(null)}>
        <MuiAlert onClose={() => setAckMessage(null)} severity={ackMessage?.severity}>
          {ackMessage?.text}
        </MuiAlert>
      </Snackbar>
    </Box>
  )
}

export const LiveNoteArray = () => {
  const { id } = useParams<{ id: string }>()
  const { data, error, loading } = useGetDisplayGroup(id)
  const [enableAcks] = useEnableDeviceAcks()
  const [disableAcks] = useDisableDeviceAcks()
  const [bulkAckLoading, setBulkAckLoading] = useState(false)
  const [bulkAckMessage, setBulkAckMessage] = useState<{ text: string; severity: 'success' | 'error' } | null>(null)
  const deviceMapRef = useRef<Record<string, string>>({})

  const handleDeviceIdResolved = useCallback((boardId: string, deviceId: string) => {
    deviceMapRef.current[boardId] = deviceId
  }, [])

  const displayGroup = data?.getDisplayGroup?.__typename === 'DisplayGroupV2' ? data.getDisplayGroup : null

  const boardGrid = useMemo(() => {
    if (!displayGroup) return []
    return displayGroup.boards
  }, [displayGroup])

  const handleBulkAcks = async (enable: boolean) => {
    const deviceIds = Object.values(deviceMapRef.current)
    if (deviceIds.length === 0) {
      setBulkAckMessage({ text: 'No devices found yet', severity: 'error' })
      return
    }
    setBulkAckLoading(true)
    const mutation = enable ? enableAcks : disableAcks
    let successCount = 0
    let failCount = 0
    for (const deviceId of deviceIds) {
      try {
        await mutation({ variables: { device: deviceId } })
        successCount++
      } catch {
        failCount++
      }
    }
    setBulkAckLoading(false)
    if (failCount === 0) {
      setBulkAckMessage({ text: `Acks ${enable ? 'enabled' : 'disabled'} on ${successCount} device(s)`, severity: 'success' })
    } else {
      setBulkAckMessage({ text: `${successCount} succeeded, ${failCount} failed`, severity: 'error' })
    }
  }

  if (error) {
    return <Alert severity='error'>{error.message || 'Note Array not found'}</Alert>
  }

  if (loading || !data) {
    return <CircularProgress />
  }

  if (!displayGroup) {
    const errorMessage =
      data?.getDisplayGroup?.__typename === 'DisplayGroupErrorV2' ? data.getDisplayGroup.error : 'Note Array not found.'
    return <Alert severity='error'>{errorMessage}</Alert>
  }

  return (
    <>
      <Typography variant='button' display='block' gutterBottom>
        <Link to='/note-arrays'>Note Arrays</Link> |{' '}
        <Link to={`/note-arrays/${id}`}>{displayGroup.title || 'Untitled'}</Link>
      </Typography>
      <Spacer size='extraLarge' />
      <Typography variant='h5' gutterBottom>
        Live View — {displayGroup.title || 'Untitled'} ({displayGroup.rows}×{displayGroup.columns})
      </Typography>
      <Typography variant='caption' display='block' gutterBottom>
        {displayGroup.boards.flat().filter(Boolean).length} boards connected via WebSocket
      </Typography>
      <Box style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <Tooltip title='The ack state is managed on device — iterates through all devices in this note array' arrow>
          <Button
            variant='contained'
            color='primary'
            size='small'
            disabled={bulkAckLoading}
            onClick={() => handleBulkAcks(true)}>
            {bulkAckLoading ? <CircularProgress size={18} /> : 'Enable Acks (All Devices)'}
          </Button>
        </Tooltip>
        <Tooltip title='The ack state is managed on device — iterates through all devices in this note array' arrow>
          <Button
            variant='contained'
            size='small'
            disabled={bulkAckLoading}
            onClick={() => handleBulkAcks(false)}>
            {bulkAckLoading ? <CircularProgress size={18} /> : 'Disable Acks (All Devices)'}
          </Button>
        </Tooltip>
      </Box>
      <Snackbar open={!!bulkAckMessage} autoHideDuration={4000} onClose={() => setBulkAckMessage(null)}>
        <MuiAlert onClose={() => setBulkAckMessage(null)} severity={bulkAckMessage?.severity}>
          {bulkAckMessage?.text}
        </MuiAlert>
      </Snackbar>
      <Spacer size='large' />
      {boardGrid.map((row, rowIndex) => (
        <Box
          key={rowIndex}
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: 8,
            marginBottom: 8
          }}>
          {row.map((board, colIndex) => (
            <Box
              key={`${rowIndex}-${colIndex}`}
              style={{
                flex: 1,
                minWidth: 200,
                maxWidth: 600
              }}>
              {board ? (
                <LiveBoardCell
                  boardId={board.board.id}
                  title={board.board.title}
                  boardStyle={board.board.boardStyle}
                  connectDelay={(rowIndex * displayGroup.columns + colIndex) * 500}
                  onDeviceIdResolved={handleDeviceIdResolved}
                />
              ) : (
                <Box
                  style={{
                    border: '1px dashed #ccc',
                    borderRadius: 4,
                    padding: 16,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 80
                  }}>
                  <Typography color='textSecondary'>Empty</Typography>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      ))}
    </>
  )
}
